import { t, Trans } from '@lingui/macro';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { get, isEmpty, isNil } from 'lodash';
import HelpOutlineIcon from '@material-ui/icons/HelpOutline';
import { i18n } from '@lingui/core';
import FormSection from '../../../form/FormSection';
import {
    INFRASTRUCTURE_LIST_INITIAL_SORT_FIELD_INFO,
    INFRASTRUCTURE_LIST_PARAMS,
    INFRASTRUCTURE_LIST_REQUEST_PARAMS,
    INTERFACE_DELETED_FALSE_SOURCE,
    VIRTUAL_INFRASTRUCTURE_DATA_SOURCE,
    VIRTUAL_LIST_ENABLE_DISABLE_URLS,
} from '../InfrastructureConstants';
import { ACTION_SUCCESS_MESSAGES, DISABLE, ENABLE } from '../../../../services/FormDataTableActionService';
import { useGroupAdminStyles } from '../../groups/GroupsAdminUtils';
import { useDataService } from '../../../../services/ApiDataService';
import useAlerts from '../../../../stores/AlertsStore';
import { getErrMessage, to } from '../../../../utilities/PromiseUtilities';
import { INFRASTRUCTURE_LIST_COLUMNS } from '../InfrastructureHelper';
import { getSuccessToast } from '../../../../utilities/FormUtilities';
import useUiToasts from '../../../../stores/ToastStore';
import useRecordContext from '../../../../utilities/RecordContextProviderUtilities';
import { SELECTIONMODE_MULTI } from '../../../data-table/SelectionContext';
import { getSystemTime } from '../../../../stores/SystemTimeStore';
import ListActionSelector from '../../../form/ListActionSelector';
import useActioningContext from '../../../../utilities/ActioningProviderUtilities';
import { EMPTY_ARRAY, LOADING_DATA_MSG } from '../../../../utilities/ConstantsUtilities';

const INITIAL_LOAD_TIME = -1;
const VISIBLE_ROWS = 10;
const INCREMENT_VALUE = 1;

/**
 * Builds query parameters for fetching infrastructure list
 * @param {string} parentUuid - Parent entity UUID to filter by
 * @returns {Object} Query parameters object with stringified query and request params
 */
const buildQueryParams = (parentUuid) => {
    if (isNil(parentUuid)) {
        return null;
    }

    return {
        query: JSON.stringify({
            parentUuid,
            $and: [INTERFACE_DELETED_FALSE_SOURCE],
        }),
        requestParams: INFRASTRUCTURE_LIST_REQUEST_PARAMS,
    };
};

/**
 * Determines if record ID transition should be skipped
 * Skip occurs when transitioning between two different valid record IDs
 * @param {string} currentRecordId - Current record ID
 * @param {string} previousRecordId - Previous record ID
 * @returns {boolean} True if transition should be skipped
 */
const shouldSkipRecordIdTransition = (currentRecordId, previousRecordId) => {
    const hasCurrentRecordId = !isEmpty(currentRecordId);
    const hasPreviousRecordId = !isEmpty(previousRecordId);
    const isDifferentRecordId = currentRecordId !== previousRecordId;

    return hasCurrentRecordId && hasPreviousRecordId && isDifferentRecordId;
};

const InfrastructureListSelector = ({ tableId, url, title, emptyStateMessage }) => {
    const styles = useGroupAdminStyles();
    const dataService = useDataService();
    const { id: recordId } = useRecordContext();
    const { isActioning, setIsActioning } = useActioningContext();

    const [, alertsActions] = useAlerts();
    const [, uiToastActions] = useUiToasts();

    const [infrastructures, setInfrastructures] = useState(EMPTY_ARRAY);
    const [lastLoadDataArrayTime, setLastLoadDataArrayTime] = useState(INITIAL_LOAD_TIME);
    const [emptyText, setEmptyText] = useState(LOADING_DATA_MSG);

    const requestIdRef = useRef(0);
    const previousRecordIdRef = useRef(null);

    const isRequestStale = useCallback((requestId) => {
        return requestId !== requestIdRef.current;
    }, []);

    const fetchInfrastructureList = useCallback(
        async (parentUuid) => {
            if (isEmpty(parentUuid)) {
                setEmptyText(emptyStateMessage);
                setInfrastructures(EMPTY_ARRAY);
                return;
            }

            requestIdRef.current += INCREMENT_VALUE;
            const currentRequestId = requestIdRef.current;

            setEmptyText(LOADING_DATA_MSG);
            setInfrastructures(EMPTY_ARRAY);

            const queryParams = buildQueryParams(parentUuid);
            if (isNil(queryParams)) {
                setEmptyText(emptyStateMessage);
                return;
            }

            const [error, response] = await to(dataService.get(url, queryParams));

            if (isRequestStale(currentRequestId)) {
                return;
            }

            if (error) {
                setEmptyText(undefined);
                alertsActions.createErrorAlert(getErrMessage(error));
                return;
            }

            const infrastructureData = get(response, 'data', EMPTY_ARRAY);
            setEmptyText(emptyStateMessage);
            setInfrastructures(infrastructureData);
            setLastLoadDataArrayTime(getSystemTime().valueOf());
        },
        [dataService, url, isRequestStale, emptyStateMessage, alertsActions]
    );

    const executeInfrastructureAction = useCallback(
        async (action, tableSelection) => {
            if (isEmpty(action) || isEmpty(tableSelection)) {
                return;
            }

            const actionUrl = VIRTUAL_LIST_ENABLE_DISABLE_URLS[action];
            if (isEmpty(actionUrl)) {
                alertsActions.createErrorAlert(`Invalid action: ${action}`);
                return;
            }

            setIsActioning(true);

            const actionParams = get(VIRTUAL_INFRASTRUCTURE_DATA_SOURCE, 'params');
            const [error] = await to(dataService.post(actionUrl, tableSelection, actionParams));

            if (error) {
                alertsActions.createErrorAlert(getErrMessage(error));
                setIsActioning(false);
                return;
            }

            const successMessage = getSuccessToast(action, ACTION_SUCCESS_MESSAGES);
            uiToastActions.createToast(successMessage);

            await fetchInfrastructureList(recordId);
            setIsActioning(false);
        },
        [dataService, alertsActions, uiToastActions, fetchInfrastructureList, recordId, setIsActioning]
    );

    const handleEnableAction = useCallback(
        (tableSelection) => executeInfrastructureAction(ENABLE, tableSelection),
        [executeInfrastructureAction]
    );

    const handleDisableAction = useCallback(
        (tableSelection) => executeInfrastructureAction(DISABLE, tableSelection),
        [executeInfrastructureAction]
    );

    useEffect(() => {
        if (shouldSkipRecordIdTransition(recordId, previousRecordIdRef.current)) {
            return undefined;
        }

        previousRecordIdRef.current = recordId;

        fetchInfrastructureList(recordId);

        return () => {
            requestIdRef.current += INCREMENT_VALUE;
        };
    }, [recordId, fetchInfrastructureList]);

    const actionButtons = useMemo(
        () => ({
            [ENABLE]: {
                label: <Trans>{ENABLE}</Trans>,
                actionFn: handleEnableAction,
                title: i18n._(t`ENABLE will not take action until SAVE`),
                endIcon: <HelpOutlineIcon />,
            },
            [DISABLE]: {
                label: <Trans>{DISABLE}</Trans>,
                actionFn: handleDisableAction,
                title: i18n._(t`DISABLE will not take action until SAVE`),
                endIcon: <HelpOutlineIcon />,
            },
        }),
        [handleEnableAction, handleDisableAction]
    );

    return (
        <FormSection title={title} addSpacer>
            <div className={styles.selectorWrapper}>
                <ListActionSelector
                    tableId={tableId}
                    model={SELECTIONMODE_MULTI}
                    gridColumns={INFRASTRUCTURE_LIST_COLUMNS}
                    sorters={INFRASTRUCTURE_LIST_INITIAL_SORT_FIELD_INFO}
                    dataArray={infrastructures}
                    lastLoadDataArrayTime={lastLoadDataArrayTime}
                    dataArraySearchFields={INFRASTRUCTURE_LIST_PARAMS.params.entityFields}
                    actionButtons={actionButtons}
                    isActioning={isActioning}
                    emptyText={emptyText}
                    visibleRows={VISIBLE_ROWS}
                />
            </div>
        </FormSection>
    );
};

InfrastructureListSelector.propTypes = {
    tableId: PropTypes.string.isRequired,
    url: PropTypes.string.isRequired,
    title: PropTypes.element.isRequired,
    emptyStateMessage: PropTypes.string,
};

InfrastructureListSelector.defaultProps = {
    emptyStateMessage: '',
};

export default InfrastructureListSelector;
